import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

interface EventRow {
    id: number;
    toolCode: string;
    toolName: string;
    level: string;
    source: string;
    message: string;
    context?: string;
    userEmail?: string;
    createdAt: string;
}

interface EventDetail extends EventRow {
    detail?: string;
    userId?: number;
}

interface GlobalDiagnostics {
    windowHours: number;
    totalEvents: number;
    byLevel: { level: string; count: number }[];
    recentEvents: EventRow[];
}

const LEVEL_BADGE: Record<string, string> = {
    Error: 'bg-danger',
    Warning: 'bg-warning text-dark',
    Info: 'bg-info text-dark',
};

const QualityEventsPage: React.FC = () => {
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<GlobalDiagnostics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sinceHours, setSinceHours] = useState(() => Number(searchParams.get('sinceHours')) || 24);
    const [level, setLevel] = useState(() => searchParams.get('level') || 'all');
    const [source, setSource] = useState(() => searchParams.get('source') || 'all');
    const [toolCode, setToolCode] = useState(() => searchParams.get('toolCode') || '');
    const [search, setSearch] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [detail, setDetail] = useState<EventDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.adminGetGlobalDiagnostics<GlobalDiagnostics>({
                sinceHours,
                level: level === 'all' ? undefined : level,
                source: source === 'all' ? undefined : source,
                toolCode: toolCode.trim() || undefined,
                limit: 500,
            });
            setData(response);
        } catch (loadError: unknown) {
            setError(loadError instanceof Error ? loadError.message : 'Errore caricamento eventi');
        } finally {
            setLoading(false);
        }
    }, [level, sinceHours, source, toolCode]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        if (!autoRefresh) return;
        const intervalId = window.setInterval(() => void load(), 30_000);
        return () => window.clearInterval(intervalId);
    }, [autoRefresh, load]);

    useEffect(() => {
        const next = new URLSearchParams();
        if (sinceHours !== 24) next.set('sinceHours', String(sinceHours));
        if (level !== 'all') next.set('level', level);
        if (source !== 'all') next.set('source', source);
        if (toolCode.trim()) next.set('toolCode', toolCode.trim());
        setSearchParams(next, { replace: true });
    }, [level, setSearchParams, sinceHours, source, toolCode]);

    const filteredEvents = useMemo(() => {
        const query = search.trim().toLocaleLowerCase();
        if (!query) return data?.recentEvents ?? [];
        return (data?.recentEvents ?? []).filter(event =>
            event.message.toLocaleLowerCase().includes(query)
            || event.toolName.toLocaleLowerCase().includes(query)
            || event.source.toLocaleLowerCase().includes(query)
            || (event.context ?? '').toLocaleLowerCase().includes(query)
            || (event.userEmail ?? '').toLocaleLowerCase().includes(query));
    }, [data, search]);

    const counts = useMemo(() => {
        const result: Record<string, number> = { Error: 0, Warning: 0, Info: 0 };
        for (const item of data?.byLevel ?? []) result[item.level] = item.count;
        return result;
    }, [data]);

    const openDetail = async (id: number) => {
        setDetailLoading(true);
        setDetail(null);
        try {
            setDetail(await apiService.adminGetDiagnosticEvent<EventDetail>(id));
        } catch {
            showToast('Errore', 'Dettaglio evento non disponibile', 'danger');
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="mb-1">Eventi grezzi</h5>
                    <div className="small text-body-secondary">
                        Vista tecnica degli eventi raccolti da frontend e backend, prima della correlazione.
                    </div>
                </div>
                <div className="d-flex gap-3 align-items-center">
                    <div className="form-check form-switch mb-0">
                        <input type="checkbox" className="form-check-input" id="qualityAutoRefresh"
                            checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} />
                        <label className="form-check-label small" htmlFor="qualityAutoRefresh">Auto-refresh 30s</label>
                    </div>
                    <button className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>
                        <i className="bi bi-arrow-clockwise me-1" />{loading ? 'Aggiorno…' : 'Aggiorna'}
                    </button>
                </div>
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Periodo</label>
                            <select className="form-select form-select-sm" value={sinceHours}
                                onChange={event => setSinceHours(Number(event.target.value))}>
                                <option value={1}>1 ora</option>
                                <option value={6}>6 ore</option>
                                <option value={24}>24 ore</option>
                                <option value={72}>3 giorni</option>
                                <option value={168}>7 giorni</option>
                                <option value={720}>30 giorni</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Livello</label>
                            <select className="form-select form-select-sm" value={level}
                                onChange={event => setLevel(event.target.value)}>
                                <option value="all">Tutti</option>
                                <option value="Error">Error</option>
                                <option value="Warning">Warning</option>
                                <option value="Info">Info</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Origine</label>
                            <select className="form-select form-select-sm" value={source}
                                onChange={event => setSource(event.target.value)}>
                                <option value="all">Tutte</option>
                                <option value="Client">Frontend</option>
                                <option value="Server">Backend</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-3">
                            <label className="form-label small">Tool code (esatto)</label>
                            <input className="form-control form-control-sm" value={toolCode}
                                onChange={event => setToolCode(event.target.value)} placeholder="Tutti i tool" />
                        </div>
                        <div className="col-12 col-md">
                            <label className="form-label small">Cerca</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text"><i className="bi bi-search" /></span>
                                <input className="form-control" value={search}
                                    onChange={event => setSearch(event.target.value)} placeholder="Messaggio, context o utente" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger"><i className="bi bi-exclamation-triangle me-2" />{error}</div>}

            <div className="row g-3 mb-3">
                <EventMetric label="Eventi nel periodo" value={data?.totalEvents ?? 0} color="primary" loading={loading} />
                <EventMetric label="Error" value={counts.Error} color="danger" loading={loading} />
                <EventMetric label="Warning" value={counts.Warning} color="warning" loading={loading} />
                <EventMetric label="Info" value={counts.Info} color="info" loading={loading} />
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
                    <strong>{filteredEvents.length} eventi mostrati</strong>
                    <span className="small text-body-secondary">Massimo 500 eventi</span>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover table-sm align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Quando</th>
                                <th>Tool</th>
                                <th>Livello</th>
                                <th>Origine</th>
                                <th>Messaggio</th>
                                <th>Context</th>
                                <th>Utente</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-4">
                                    <span className="spinner-border spinner-border-sm me-2" />Caricamento…
                                </td></tr>
                            )}
                            {!loading && filteredEvents.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-4">
                                    Nessun evento corrisponde ai filtri.
                                </td></tr>
                            )}
                            {!loading && filteredEvents.map(event => (
                                <tr key={event.id} role="button" onClick={() => void openDetail(event.id)}>
                                    <td className="small text-body-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(event.createdAt).toLocaleString('it-IT')}
                                    </td>
                                    <td>
                                        <div className="small fw-semibold">{event.toolName}</div>
                                        <code className="small text-body-secondary">{event.toolCode}</code>
                                    </td>
                                    <td><span className={`badge ${LEVEL_BADGE[event.level] ?? 'bg-secondary'}`}>{event.level}</span></td>
                                    <td className="small">{event.source}</td>
                                    <td className="small" style={{ minWidth: 300 }}>
                                        <code title={event.message}>
                                            {event.message.length > 100 ? `${event.message.slice(0, 100)}…` : event.message}
                                        </code>
                                    </td>
                                    <td className="small text-body-secondary">{event.context ?? '—'}</td>
                                    <td className="small">{event.userEmail ?? '—'}</td>
                                    <td><i className="bi bi-chevron-right text-body-secondary" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal show={detail !== null || detailLoading} onHide={() => setDetail(null)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Dettaglio evento</Modal.Title></Modal.Header>
                <Modal.Body>
                    {detailLoading && <div className="text-center p-4"><span className="spinner-border" /></div>}
                    {detail && (
                        <>
                            <div className="row g-2 mb-3 small">
                                <div className="col-md-6"><strong>Tool:</strong> {detail.toolName} (<code>{detail.toolCode}</code>)</div>
                                <div className="col-md-3"><strong>Livello:</strong>{' '}
                                    <span className={`badge ${LEVEL_BADGE[detail.level] ?? 'bg-secondary'}`}>{detail.level}</span>
                                </div>
                                <div className="col-md-3"><strong>Origine:</strong> {detail.source}</div>
                                <div className="col-md-6"><strong>Quando:</strong> {new Date(detail.createdAt).toLocaleString('it-IT')}</div>
                                <div className="col-md-6"><strong>Utente:</strong> {detail.userEmail ?? '—'}</div>
                                {detail.context && <div className="col-12"><strong>Context:</strong> <code>{detail.context}</code></div>}
                            </div>
                            <h6 className="small text-uppercase text-body-secondary">Messaggio</h6>
                            <pre className="bg-body-tertiary border rounded p-2 small font-monospace"
                                style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>{detail.message}</pre>
                            {detail.detail && (
                                <>
                                    <h6 className="small text-uppercase text-body-secondary">Dettaglio sanificato</h6>
                                    <pre className="bg-body-tertiary border rounded p-2 small font-monospace mb-0"
                                        style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>{detail.detail}</pre>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setDetail(null)}>Chiudi</Button></Modal.Footer>
            </Modal>
        </>
    );
};

const EventMetric: React.FC<{ label: string; value: number; color: string; loading: boolean }> =
    ({ label, value, color, loading }) => (
        <div className="col-6 col-xl-3">
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <div className="small text-body-secondary">{label}</div>
                    <div className={`fs-4 fw-bold text-${color}`}>
                        {loading ? <span className="placeholder col-4" /> : value}
                    </div>
                </div>
            </div>
        </div>
    );

export default QualityEventsPage;
